/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {signal} from '@preact/signals-react';
import {createMutator} from '@ion/applications/src/store/SignalUtils';
import {getLocalStorage, setLocalStorage} from '@ion/core/src/capabilities/storage';
import {logReloadBehaviorChange, logThemeChange} from './DLSThemeLogger';
import {DLSThemes} from './types';

// dev-phase only: persists the developer's selected theme across reloads
// instead of always starting from legacy - remove once the current theme
// is the sole/default theme and this toggle is no longer needed ...
const THEME_STORAGE_KEY = 'ion-dls-selected-theme';

const getStoredTheme = (): DLSThemes | undefined => {
  const stored = getLocalStorage(THEME_STORAGE_KEY);
  return Object.values(DLSThemes).includes(stored) ? stored : undefined;
};

/**
 * Tracks whether multi-theme switching is enabled. When `false`, the
 * application uses only the legacy theme. When `true`, theme switching
 * between legacy and current themes is enabled.
 */
export const multiThemeEnabled = signal<boolean>(true);

/**
 * Mutator function for enabling or disabling multi-theme support.
 */
export const setMultiThemeEnabled = createMutator(multiThemeEnabled);

export const addMultiThemeEnabledObserver = (callback: (enabled: boolean) => void) => {
  return multiThemeEnabled.subscribe(callback);
};

/**
 * Tracks the currently selected theme identifier.
 *
 * Values: Themes.legacy or Themes.current
 *
 * Initializes from the developer's last selection in localStorage when
 * present, falling back to Themes.legacy otherwise.
 */
export const currentTheme = signal<DLSThemes>(getStoredTheme() || DLSThemes.legacy);

/**
 * Mutator function for updating the currently selected theme. Persists the
 * selection to localStorage so it survives a reload instead of always
 * starting from Themes.legacy.
 */
export const setCurrentTheme = (newTheme: DLSThemes): void => {
  const oldTheme = currentTheme.value;
  currentTheme.value = newTheme;
  setLocalStorage(THEME_STORAGE_KEY, newTheme);
  logThemeChange(oldTheme, newTheme);
};

/**
 * Registers a callback to be invoked when the current theme changes.
 *
 * ```typescript
 * import {addThemeChangeObserver} from '@ion/dls/src/theme/ThemeStore';
 * import {DLSThemes} from '@ion/dls/src/theme/types';
 *
 * const unsubscribe = addThemeChangeObserver((theme: DLSThemes) => {
 *   console.log('Theme changed to:', theme);
 * });
 *
 * // cleanup...
 * unsubscribe();
 * ```
 *
 * @param   callback Function invoked when the theme changes, receives the new
 *          theme identifier.
 * @returns Unsubscribe function to remove the observer.
 */
export const addThemeChangeObserver = (callback: (theme: DLSThemes) => void) => {
  return currentTheme.subscribe(callback);
};

/**
 * Tracks whether a page reload should occur when theme changes.
 */
export const shouldReloadOnThemeChange = signal<boolean>(false);

/**
 * Mutator function for updating the reload behavior on theme change.
 */
export const setShouldReloadOnThemeChange = (enabled: boolean): void => {
  shouldReloadOnThemeChange.value = enabled;
  logReloadBehaviorChange(enabled);
};
